import type { SensorData } from './analysisTabSensorTypes';

export type UnitScalingFormValues = {
  [key: string]: { min: string; max: string; displayName?: string; actualUnit?: string };
};

export function getMinMaxForUnitFromRanges(
  unit: string,
  uomRanges: { uom: string; minValue: number; maxValue: number }[]
): { min: number | ''; max: number | '' } {
  const actualUnit = unit === 'Other' ? '' : unit;
  const uomRange = uomRanges.find(range => range.uom === actualUnit);
  if (uomRange) {
    return {
      min: Math.floor(uomRange.minValue),
      max: Math.ceil(uomRange.maxValue),
    };
  }
  return { min: '', max: '' };
}

export function buildUnitScalingInitialValues(
  selectedSensorsList: SensorData[],
  manuallyEnteredScalingValues: { [unit: string]: { min: string; max: string } },
  uomRanges: { uom: string; minValue: number; maxValue: number }[]
): UnitScalingFormValues {
  const initialValues: UnitScalingFormValues = {};
  const processedKeys = new Set<string>();

  for (const sensor of selectedSensorsList) {
    const unit = sensor.unit?.trim() || '';
    let displayKey: string;
    let displayName: string;
    let actualUnit: string;

    if (unit === '') {
      displayKey = sensor.sensorName;
      displayName = 'Others';
      actualUnit = '';
    } else {
      displayKey = unit;
      displayName = unit;
      actualUnit = unit;
    }

    if (processedKeys.has(displayKey)) {
      continue;
    }
    processedKeys.add(displayKey);

    if (manuallyEnteredScalingValues[displayKey]) {
      initialValues[displayKey] = {
        min: manuallyEnteredScalingValues[displayKey].min,
        max: manuallyEnteredScalingValues[displayKey].max,
        displayName,
        actualUnit,
      };
      continue;
    }

    const { min, max } = getMinMaxForUnitFromRanges(actualUnit, uomRanges);
    initialValues[displayKey] = {
      min: min.toString(),
      max: max.toString(),
      displayName,
      actualUnit,
    };
  }

  return initialValues;
}

export function isCustomScalingApplyEnabled(unitScalingValues: UnitScalingFormValues): boolean {
  for (const values of Object.values(unitScalingValues)) {
    const minValue = values.min.trim();
    const maxValue = values.max.trim();

    if (minValue !== '' && maxValue === '') {
      return false;
    }
    if (maxValue !== '' && minValue === '') {
      return false;
    }
    if (minValue !== '' && maxValue !== '') {
      const min = parseFloat(minValue);
      const max = parseFloat(maxValue);
      if (isNaN(min) || isNaN(max) || max <= min) {
        return false;
      }
    }
  }

  return Object.values(unitScalingValues).some(values => {
    const minValue = values.min.trim();
    const maxValue = values.max.trim();
    return minValue !== '' && maxValue !== '';
  });
}

export function getModifiedValidScalingUnits(
  unitScalingValues: UnitScalingFormValues,
  modifiedUnits: Set<string>
): [string, UnitScalingFormValues[string]][] {
  const result: [string, UnitScalingFormValues[string]][] = [];
  for (const [unit, values] of Object.entries(unitScalingValues)) {
    if (!modifiedUnits.has(unit)) {
      continue;
    }
    const min = parseFloat(values.min);
    const max = parseFloat(values.max);
    if (!isNaN(min) && !isNaN(max) && max > min) {
      result.push([unit, values]);
    }
  }
  return result;
}
